import { useState, useEffect, useCallback, useRef } from "react"

/**
 * `useIdleTimer` is a hook that determines if the user is idle based on lack of specific activities (e.g., mouse movement, key presses).
 *
 * @param idleTime - The duration in milliseconds after which the user is considered idle.
 */

function useIdleTimer(idleTime: number) {
  const [isIdle, setIsIdle] = useState(false)
  const idleTimerRef = useRef<ReturnType<typeof setTimeout>>()

  const handleActivity = useCallback(() => {
    setIsIdle(false)
    if (!idleTime) return null
    if (idleTimerRef.current) clearTimeout(idleTimerRef.current)

    idleTimerRef.current = setTimeout(() => {
      setIsIdle(true)
    }, idleTime)
  }, [idleTime])

  useEffect(() => {
    const events = ["mousemove", "keydown", "scroll"]
    events.forEach((event) => window.addEventListener(event, handleActivity))

    handleActivity()

    return () => {
      if (idleTimerRef.current) clearTimeout(idleTimerRef.current)
      events.forEach((event) => window.removeEventListener(event, handleActivity))
    }
  }, [handleActivity])

  return { isIdle, refreshTimer: handleActivity }
}

export { useIdleTimer }
